import { extractAccessTokenFromLoginJson } from '../api/authLoginResponse';
import { decodeJwtPayload } from '../auth/jwtPayload';

const REGISTER_RESPONSE_WRAPPER_KEYS = [
  'data',
  'result',
  'user',
  'business_client',
  'businessClient',
  'client',
  'payload',
  'profile',
] as const;

const EXPLICIT_IDENTIFIER_KEYS = [
  'business_client_identifier',
  'businessClientIdentifier',
  'registration_identifier',
  'registrationIdentifier',
  'worker_registration_code',
  'workerRegistrationCode',
  'employer_code',
  'employerCode',
  'business_client_code',
  'businessClientCode',
  'registration_code',
  'registrationCode',
  'employer_registration_code',
  'employerRegistrationCode',
  'worker_invite_code',
  'workerInviteCode',
  'invite_code',
  'inviteCode',
  'company_code',
  'companyCode',
] as const;

const UUID_SOURCE_KEYS = [
  'id',
  'user_id',
  'userId',
  'uuid',
  'client_id',
  'clientId',
  'business_client_id',
  'businessClientId',
  'sub',
] as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pickTrimmedString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

/** Normalize API/user input to exactly eight numeric digits, or null when invalid. */
export function normalizeEightDigitBusinessClientIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const digits = String(Math.abs(Math.trunc(value))).replace(/\D/g, '');
    if (digits.length === 0) {
      return null;
    }
    const normalized = digits.length >= 8 ? digits.slice(-8) : digits.padStart(8, '0');
    return /^\d{8}$/.test(normalized) ? normalized : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const digits = value.trim().replace(/\D/g, '');
  if (digits.length < 8) {
    return null;
  }
  const normalized = digits.length === 8 ? digits : digits.slice(-8);
  return /^\d{8}$/.test(normalized) ? normalized : null;
}

export function isValidEightDigitBusinessClientIdentifier(value: unknown): boolean {
  return normalizeEightDigitBusinessClientIdentifier(value) !== null;
}

/** Deterministic 8-digit code from a UUID, numeric id, or other stable account key. */
export function deriveEightDigitIdentifierFromUuid(uuid: string): string | null {
  const trimmed = uuid.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = /^[0-9a-f-]{32,36}$/i.test(trimmed)
    ? trimmed.toLowerCase().replace(/[^0-9a-f]/g, '')
    : trimmed;
  if (!normalized) {
    return null;
  }
  let hash = 2166136261;
  for (let i = 0; i < normalized.length; i += 1) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return String(hash >>> 0).padStart(8, '0').slice(-8);
}

function collectRegisterRecords(parsed: unknown): Record<string, unknown>[] {
  const records: Record<string, unknown>[] = [];
  const seen = new Set<Record<string, unknown>>();

  const visit = (value: unknown) => {
    if (!isPlainObject(value) || seen.has(value)) {
      return;
    }
    seen.add(value);
    records.push(value);
    for (const key of REGISTER_RESPONSE_WRAPPER_KEYS) {
      visit(value[key]);
    }
  };

  visit(parsed);
  return records;
}

export function extractUuidFromRegisterResponse(parsed: unknown): string | null {
  for (const record of collectRegisterRecords(parsed)) {
    for (const key of UUID_SOURCE_KEYS) {
      const value = pickTrimmedString(record[key]);
      if (value) {
        return value;
      }
    }
  }
  return null;
}

/** Read an explicit 8-digit code from register/profile JSON when the API returns one. */
export function extractExplicitBusinessClientIdentifier(parsed: unknown): string | null {
  for (const record of collectRegisterRecords(parsed)) {
    for (const key of EXPLICIT_IDENTIFIER_KEYS) {
      const normalized = normalizeEightDigitBusinessClientIdentifier(record[key]);
      if (normalized) {
        return normalized;
      }
    }
  }
  return null;
}

/** Prefer API code; otherwise derive from nested id/uuid fields or register JWT. */
export function extractBusinessClientRegistrationIdentifier(parsed: unknown): string | null {
  const explicit = extractExplicitBusinessClientIdentifier(parsed);
  if (explicit) {
    return explicit;
  }
  const uuid = extractUuidFromRegisterResponse(parsed);
  if (uuid) {
    return deriveEightDigitIdentifierFromUuid(uuid);
  }
  return null;
}

/** Resolve worker code from register HTTP body (JSON, JWT, or plain 8 digits). */
export function extractBusinessClientRegistrationIdentifierFromRegisterResponse(
  responseText: string,
): string | null {
  const trimmed = responseText.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d{8}$/.test(trimmed)) {
    return trimmed;
  }

  const parsed = parseRegisterResponseJson(trimmed);
  const fromJson = extractBusinessClientRegistrationIdentifier(parsed);
  if (fromJson) {
    return fromJson;
  }

  const accessToken = parsed ? extractAccessTokenFromLoginJson(parsed) : null;
  if (accessToken) {
    const jwtPayload = decodeJwtPayload(accessToken);
    if (jwtPayload) {
      const fromJwt = extractBusinessClientRegistrationIdentifier(jwtPayload);
      if (fromJwt) {
        return fromJwt;
      }
    }
  }

  return null;
}

export function parseRegisterResponseJson(responseText: string): unknown {
  const trimmed = responseText.trim();
  if (!trimmed) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}
